<template>
  <div v-if="isLoaded" :class="!hasError ? 'sm:rounded-md p-4 border bg-white mx-12 my-6' : ''">
    <div>
      <!-- Feature comparison (up to lg) -->
      <section class="lg:hidden">
        <div class="mx-auto max-w-2xl space-y-16 px-4 py-5 sm:px-6 sm:py-16">
          <div
            v-for="(template, templateIndex) in roleTemplates"
            :key="templateIndex"
            :class="[templateIndex === 0 ? '' : 'border-t']"
          >
            <div class="-mt-px border-t-2 border-transparent pt-6 text-center">
              <img :src="`/images/users/user-${templateIndex + 1}.png`" alt="" class="mx-auto h-32" />
              <h3 class="mt-4 text-sm font-bold text-foreground">
                {{ $t(`pages.settings.roles.${template.roleTemplate.slug}`) }}
              </h3>
            </div>
            <BaseDisclosure
              v-for="(feature, featureId) in features"
              :key="featureId"
              class="my-5"
              :title="$t(`pages.settings.permissions.${feature.slug}`)"
              :icon="findEmojiByFeature(feature)?.content"
              :icon-color="findEmojiByFeature(feature)?.color"
              :is-default-open="findEmojiByFeature(feature)?.isOpen"
            >
              <!-- Fake card background -->
              <div class="p-4">
                <div
                  class="rounded-lg bg-white px-4 py-3 sm:rounded-none sm:bg-transparent sm:p-0 sm:shadow-none sm:ring-0"
                >
                  <dl class="divide-y divide-border">
                    <div
                      v-for="(permission, permissionId) in feature.permissions"
                      :key="permissionId"
                      class="flex justify-between py-3 sm:grid sm:grid-cols-2"
                    >
                      <dt class="text-sm font-medium text-foreground-secondary sm:pr-4">
                        {{ $t(`pages.settings.permissions.${permission.slug}`) }}
                      </dt>
                      <dd class="text-center sm:px-4">
                        <Checkbox
                          :id="`role[${template.roleTemplate.slug}]-module[${feature.id}]-permission[${permission.id}]`"
                          :model-value="isChecked(template.roleTemplate.id, feature.id, permission.id)"
                          class="mx-auto"
                          @update:model-value="onChange(template.roleTemplate.id, feature.id, permission.id)"
                        />
                      </dd>
                    </div>
                  </dl>
                </div>

                <!-- Fake card border -->
                <div aria-hidden="true" class="pointer-events-none absolute inset-0 hidden">
                  <div class="absolute right-0 h-full w-1/2 rounded-lg border" />
                </div>
              </div>
            </BaseDisclosure>
          </div>
        </div>
      </section>
      <!-- Feature comparison (lg+) -->
      <section class="hidden lg:block">
        <div class="mx-auto max-w-7xl px-8 pb-24">
          <div class="flex w-full items-stretch">
            <div class="-mt-px flex w-1/5 items-end pb-6 pr-4 pt-24" />
            <div
              v-for="(template, templateIndex) in roleTemplates"
              :key="templateIndex"
              aria-hidden="true"
              class="-mt-px w-1/5 pb-10 pl-4 pr-4"
            >
              <div class="border-transparent">
                <img :src="`/images/users/user-${templateIndex + 1}.png`" alt="" class="mx-auto h-32" />
                <p class="my-2 text-center text-sm font-bold leading-6 text-foreground">
                  {{ $t(`pages.settings.roles.${template.roleTemplate.slug}`) }}
                </p>
              </div>
            </div>
          </div>
          <BaseDisclosure
            v-for="(feature, featureId) in features.filter((f) => moduleAccessesForFeature(f))"
            :key="featureId"
            class="my-5"
            :title="$t(`pages.settings.permissions.${feature.slug}`)"
            :icon="findEmojiByFeature(feature)?.content"
            :icon-color="findEmojiByFeature(feature)?.color"
            :is-default-open="findEmojiByFeature(feature)?.isOpen"
          >
            <div class="p-4">
              <div class="pointer-events-none absolute inset-0 flex items-stretch" aria-hidden="true">
                <div class="w-1/5 pr-4" />
              </div>

              <table class="relative w-full">
                <thead>
                  <tr class="text-left">
                    <th scope="col">
                      <span class="sr-only">Feature</span>
                    </th>
                    <th v-for="(template, templateIndex) in roleTemplates" :key="templateIndex" scope="col">
                      <span class="sr-only">{{ templateIndex }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-border">
                  <tr v-for="(permission, permissionId) in feature.permissions" :key="permissionId">
                    <th scope="row" class="w-1/5 py-3 pr-4 text-left text-sm font-medium text-foreground-secondary">
                      {{ $t(`pages.settings.permissions.${permission.slug}`) }}
                    </th>
                    <td v-for="template in roleTemplates" :key="template" class="relative w-1/5 px-4 py-0 text-center">
                      <span class="relative flex h-full w-full items-center justify-center bg-muted py-3">
                        <Checkbox
                          :id="`role[${template.roleTemplate.slug}]-module[${feature.id}]-permission[${permission.id}]`"
                          :model-value="isChecked(template.roleTemplate.id, feature.id, permission.id)"
                          @update:model-value="onChange(template.roleTemplate.id, feature.id, permission.id)"
                        />
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </BaseDisclosure>
        </div>
      </section>
    </div>
  </div>
  <TheLoadingPage v-else page="UsersListPage" />
</template>

<script>
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import useAccess from '@/composables/access'
import useNotification from '@/composables/notification'

import { Checkbox } from '@/ui/checkbox'
import { BaseDisclosure } from '@/ui/accordion'
import useError from '@/composables/error'

export default {
  components: {
    Checkbox,
    TheLoadingPage,
    BaseDisclosure
  },
  setup() {
    const notification = useNotification()
    const { updatePermissions } = useAccess()
    const { hasError } = useError()
    const { moduleAccesses } = useLocalStorage()

    return {
      notification,
      updatePermissions,
      hasError,
      moduleAccesses
    }
  },
  data: () => {
    return {
      isLoaded: false,
      emoji: [
        {
          id: 1,
          content: 'HeroChartPieIconSolid',
          color: '#77ACF1',
          isOpen: true
        },
        {
          id: 2,
          content: 'HeroUsersIconSolid',
          color: '#511281',
          isOpen: true
        },
        {
          id: 3,
          content: 'HeroChatBubbleOvalLeftEllipsisIconSolid',
          color: '#F6AE99'
        },
        {
          id: 4,
          content: 'HeroMapPinIconSolid',
          color: '#b7deac'
        },
        {
          id: 5,
          content: 'HeroDocumentChartBarIconSolid',
          color: '#A12568'
        },
        {
          id: 6,
          content: 'HeroStarIconSolid',
          color: '#FFA900'
        },
        {
          id: 7,
          content: 'HeroBoltIconSolid',
          color: '#FC92E3'
        },
        {
          id: 8,
          content: 'HeroArrowDownTrayIconSolid',
          color: '#38A3A5'
        },
        {
          id: 9,
          content: 'HeroGiftIconSolid',
          color: '#8B5CF6'
        },
        {
          id: 10,
          content: 'HeroQrCodeIconSolid',
          color: '#50CB93'
        },
        {
          id: 11,
          content: 'HeroAdjustmentsVerticalIconSolid',
          color: '#889EAF'
        },
        {
          id: 12,
          content: 'HeroPhoneXMarkIconSolid',
          color: '#396EB0'
        },
        {
          id: 13,
          content: 'HeroMegaphoneIconOutline',
          color: '#A084CF'
        },
        {
          id: 14,
          content: 'HeroShoppingBagIconOutline',
          color: '#FFC18E'
        },
        {
          id: 15,
          content: 'HeroStarIconSolid',
          color: '#FFA900'
        },
        {
          id: 16,
          content: 'HeroStarIconSolid',
          color: '#FFA900'
        },
        {
          id: 17,
          content: 'HeroArrowsPointingOutIconSolid',
          color: '#116D6E'
        }
      ],
      features: [],
      roleTemplates: []
    }
  },
  mounted() {
    this.getPermissions()
    this.getModulesName()
  },
  computed: {
    moduleAccessesForFeature() {
      return (feature) => this.moduleAccesses?.[feature?.slug] === true
    }
  },
  methods: {
    getModulesName() {
      const hiddenModulesSlugs = ['order', 'checkin']

      this.axios.get('/modules').then((response) => {
        response.data.data.forEach((data) => {
          const canBeAdded = !hiddenModulesSlugs.includes(data.slug)
          if (canBeAdded) {
            this.features.push(data)
          }
        })
      })
    },
    getPermissions() {
      this.axios
        .get('role-templates')
        .then((response) => {
          this.roleTemplates = response.data.data
        })
        .finally(() => (this.isLoaded = true))
    },
    getUserPermissions() {
      this.axios.get('users/permissions').then((response) => {
        this.updatePermissions(response.data.data)
      })
    },
    isChecked(roleTemplateId, featureId, permissionId) {
      const filteredRoleTemplate = this.roleTemplates.filter(
        (roleTemplate) => roleTemplate.roleTemplate.id === roleTemplateId
      )[0]
      if (!filteredRoleTemplate) {
        return false
      }
      return (
        filteredRoleTemplate.permissions.filter(
          (permission) => permission.moduleId == featureId && permission.permissionId == permissionId
        ).length === 1
      )
    },
    onChange(roleTemplateId, featureId, permissionId) {
      const isAuthorized = this.isChecked(roleTemplateId, featureId, permissionId)
      this.submit(roleTemplateId, featureId, permissionId, !isAuthorized)
    },
    findEmojiByFeature(feature) {
      return this.emoji.find((em) => em.id == feature.id)
    },
    submit(roleTemplateId, featureId, permissionId, isAuthorized) {
      let filtered = []
      this.roleTemplates.map((permission) => {
        permission.permissions.map((item) => {
          if (item.module == featureId && item.permission == permissionId) {
            filtered.push(permission.template.id)
          }
        })
      })
      this.axios
        .put(`role-templates/${roleTemplateId}`, {
          moduleId: featureId,
          permissionId: permissionId,
          isAuthorized: isAuthorized
        })
        .then(() => {
          this.getUserPermissions()
          this.getPermissions()
          this.notification.create('success', {
            text: this.$t('notifications.save.success'),
            timeout: 3000
          })
        })
        .catch(() => {
          this.notification.create('error', {
            text: this.$t('notifications.save.error')
          })
        })
    }
  }
}
</script>
